/**
 * POS-sourced menu entities (Foodics / TabSense) and partner catalogs: read-only
 * in the dashboard. Delete stays locked while the source still owns the row.
 */

export function isPosReadOnlyEntity(entity) {
  if (!entity || typeof entity !== "object") return false;
  return entity.is_foodics === true || entity.is_tabsense === true || entity.is_partner === true;
}

export function isPosIntegrationDeleteLocked(entity) {
  if (!entity || typeof entity !== "object") return false;
  return (
    entity.foodics_integration_enabled === true ||
    entity.tabsense_integration_enabled === true ||
    entity.is_partner === true
  );
}

/** i18n key for read-only catalog banner (partner vs POS). */
export function catalogReadOnlyBannerKey(catalog) {
  if (catalog?.is_partner === true) return "catalogReadOnlyPartnerBanner";
  return "catalogReadOnlyPosBanner";
}

/** i18n key for auto-accept lock when the branch is partner-managed. */
export function branchPartnerAutoAcceptTooltipKey(branch) {
  if (!branch) return "partnerAutoAcceptDisabledCatalogAndOrders";
  const catalog = branch.partner_catalog === true;
  const orders = branch.partner_orders === true;
  if (catalog && orders) return "partnerAutoAcceptDisabledCatalogAndOrders";
  if (orders) return "partnerAutoAcceptDisabledOrders";
  if (catalog) return "partnerAutoAcceptDisabledCatalog";
  return "partnerAutoAcceptDisabledCatalogAndOrders";
}

/** Tooltip for disabled View / Edit when the row is owned by a POS or partner */
export function posReadOnlyTooltip(t, entity) {
  if (entity?.is_partner === true) return t("partnerIncompatibleMenuGroup");
  if (entity?.is_tabsense === true) return t("tabSenseIncompatibleMenuGroup");
  return t("foodicsIncompatibleMenuGroup");
}

/** Tooltip for disabled Delete while a POS or partner still owns the row */
export function posDeleteLockedTooltip(t, entity) {
  if (entity?.is_partner === true) return t("partnerIncompatibleMenuGroup");
  if (entity?.tabsense_integration_enabled === true) {
    return t("tabSenseIncompatibleMenuGroup");
  }
  if (entity?.foodics_integration_enabled === true) {
    return t("foodicsIncompatibleMenuGroup");
  }
  return t("foodicsIncompatibleMenuGroup");
}

/** Partner catalog on this branch and/or partner order handler (auto-accept, integrations). */
export function isBranchPartnerManaged(branch) {
  return branch?.partner_catalog === true || branch?.partner_orders === true;
}

/** Partner owns order fulfillment (accept/reject/ready) on this branch. */
export function isBranchPartnerOrderHandled(branch) {
  return branch?.partner_orders === true;
}

/** Order stored in TabSense POS (`tabsense_reference_id`) — accept/reject/ready flow is driven from TabSense. */
export function isTabSenseManagedOrder(order) {
  if (!order || typeof order !== "object") return false;
  const ref = order.tabsense_reference_id;
  return !!(ref && String(ref).trim());
}

/** Order branch uses partner order handler (`partner_managed` from the orders API). */
export function isPartnerManagedOrder(order) {
  return order?.partner_managed === true;
}

/** Dashboard accept / reject / ready / delete stay off for TabSense and partner-handled orders. */
export function isDashboardFulfillmentLocked(order) {
  return isTabSenseManagedOrder(order) || isPartnerManagedOrder(order);
}

export function fulfillmentLockTooltipKey(order) {
  if (isPartnerManagedOrder(order)) return "partnerOrderFulfillmentDisabled";
  if (isTabSenseManagedOrder(order)) return "tabSenseOrderFulfillmentDisabled";
  return "tabSenseOrderFulfillmentDisabled";
}

function hasActiveSyncedApp(branch, namePattern) {
  if (!branch?.syncedApps || !Array.isArray(branch.syncedApps)) return false;
  return branch.syncedApps.some(
    (sync) =>
      sync.app &&
      namePattern.test(sync.app) &&
      sync.synced_status === true &&
      sync.external_id
  );
}

/** Active TabSense POS mapping on branch (matches backend isBranchConnectedToTabSense). */
export function isBranchTabSenseIntegrated(branch) {
  return hasActiveSyncedApp(branch, /tabsense/i);
}

/** Active Foodics POS mapping on branch (matches backend isBranchConnectedToFoodics syncedApps check). */
export function isBranchFoodicsIntegrated(branch) {
  if (!branch) return false;
  if (branch.is_foodics_branch === true) return true;
  if (branch.source_app && String(branch.source_app).toLowerCase() === "foodics") {
    return true;
  }
  return hasActiveSyncedApp(branch, /foodics/i);
}

/** Active POS on branch: "foodics" | "tabsense" | null */
export function getBranchActivePosType(branch) {
  if (isBranchFoodicsIntegrated(branch)) return "foodics";
  if (isBranchTabSenseIntegrated(branch)) return "tabsense";
  return null;
}

/** Why auto-accept toggle is locked for POS-mapped branches, or null if not locked. */
export function isBranchPosAutoAcceptLocked(branch) {
  return getBranchActivePosType(branch);
}

export function normalizePosApplicationName(application) {
  const name = String(application || "").toLowerCase();
  if (name.includes("foodics")) return "foodics";
  if (name.includes("tabsense")) return "tabsense";
  return null;
}

/** True when this POS app conflicts with the branch's active POS mapping. */
export function isConflictingPosAppForBranch(branch, application) {
  const active = isBranchPosAutoAcceptLocked(branch);
  const requested = normalizePosApplicationName(application);
  if (!active || !requested || active === requested) return false;
  return true;
}

/** i18n key for auto-accept disabled tooltip when branch is POS-mapped. */
export function branchPosAutoAcceptTooltipKey(branch) {
  const locked = isBranchPosAutoAcceptLocked(branch);
  if (locked === "foodics") return "Auto Accept should be managed in Foodics";
  if (locked === "tabsense") return "Auto Accept is not available for TabSense integrated branches";
  return "posAutoAcceptDisabledForMappedBranch";
}

/** i18n key when Integrate/map is blocked due to another POS on the branch. */
export function branchPosConflictTooltipKey(branch, application) {
  const active = isBranchPosAutoAcceptLocked(branch);
  const requested = normalizePosApplicationName(application);
  if (!active || !requested || active === requested) return null;
  if (requested === "foodics") return "posIntegrateBlockedTabSenseOnBranch";
  if (requested === "tabsense") return "posIntegrateBlockedFoodicsOnBranch";
  return null;
}

/**
 * Store may have Foodics and TabSense together; different branches hold each POS.
 * @returns {null} — never block store-level connect for the other POS.
 */
export function storePosConflictTooltipKey(_hasFoodics, _hasTabSense, _application) {
  return null;
}

/** Omit the other POS shop id so PUT /branch does not send dual POS fields. */
export function omitOppositePosShopIdsFromBranchPayload(payload, platformOrApp) {
  if (!payload || typeof payload !== "object") return payload;
  const pos = normalizePosApplicationName(platformOrApp);
  if (pos === "foodics") {
    delete payload.tabsenseShopId;
  } else if (pos === "tabsense") {
    delete payload.foodicsBranchId;
  }
  return payload;
}
